import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";

const categories = ["All", "Web Development", "Mobile App", "E-commerce", "Software"];

// Authentic portfolio projects from VariableSoft website
const portfolioProjects = [
  {
    id: 1,
    title: "Agri Bolo",
    description: "Agricultural platform connecting farmers with modern farming solutions and market access.",
    category: "Web Development",
    image: "/Dashboard-Purchases.png",
    technologies: ["Web Development", "Dashboard", "E-commerce"],
    link: "#"
  },
  {
    id: 2,
    title: "College Raptor",
    description: "Educational platform helping students find and compare colleges based on their preferences.",
    category: "Web Development",
    image: "/College-Raptor.jpg",
    technologies: ["Education", "Platform", "Data Analytics"],
    link: "#"
  },
  {
    id: 3,
    title: "Prestloans",
    description: "Financial services platform providing easy loan processing and management solutions.",
    category: "Software",
    image: "/Prestloans.png",
    technologies: ["Fintech", "Loan Management", "Financial Services"],
    link: "#"
  },
  {
    id: 4,
    title: "Genus Power",
    description: "Power management and utility solutions for efficient energy distribution.",
    category: "Software",
    image: "/genuspower-logo.png",
    technologies: ["Energy", "Utility Management", "Enterprise"],
    link: "#"
  },
  {
    id: 5,
    title: "Kalam Security",
    description: "Advanced security solutions and management systems for modern enterprises.",
    category: "Software",
    image: "/KALAMLOGO2.png",
    technologies: ["Security", "Enterprise", "Management"],
    link: "#"
  },
  {
    id: 6,
    title: "Gravity Computers",
    description: "Computer hardware and software solutions for business and personal use.",
    category: "E-commerce",
    image: "/gravity.png",
    technologies: ["Hardware", "Software", "E-commerce"],
    link: "#"
  },
  {
    id: 7,
    title: "VKALP",
    description: "Digital transformation solutions helping businesses modernize their operations.",
    category: "Web Development",
    image: "/VKALP.jpg",
    technologies: ["Digital Transformation", "Business Solutions"],
    link: "#"
  },
  {
    id: 8,
    title: "Mirus Project",
    description: "Healthcare management system providing comprehensive patient care solutions.",
    category: "Software",
    image: "/mirus.jpg",
    technologies: ["Healthcare", "Management System", "Patient Care"],
    link: "#"
  },
  {
    id: 9,
    title: "USD DMV System",
    description: "US-based Department of Motor Vehicles management system for efficient vehicle registration.",
    category: "Software",
    image: "/USD.jpg",
    technologies: ["Government", "DMV", "Registration System"],
    link: "#"
  },
  {
    id: 10,
    title: "Nilai University",
    description: "Educational management platform for university administration and student services.",
    category: "Web Development",
    image: "/nilai.jpg",
    technologies: ["Education", "University Management", "Student Portal"],
    link: "#"
  },
  {
    id: 11,
    title: "Aplus Technology",
    description: "Technology solutions provider offering innovative software development services.",
    category: "Web Development",
    image: "/aplus.png",
    technologies: ["Technology", "Software Development", "Innovation"],
    link: "#"
  },
  {
    id: 12,
    title: "Kartlay Shopping",
    description: "Online shopping platform with modern e-commerce features and payment integration.",
    category: "E-commerce",
    image: "/kartlay.jpg",
    technologies: ["E-commerce", "Online Shopping", "Payment Gateway"],
    link: "#"
  },
  {
    id: 13,
    title: "Elite Group",
    description: "Corporate solutions and business management platform for elite enterprises.",
    category: "Software",
    image: "/elite.jpg",
    technologies: ["Corporate", "Business Management", "Enterprise"],
    link: "#"
  },
  {
    id: 14,
    title: "RNS Infotech",
    description: "Information technology solutions and services for modern business requirements.",
    category: "Web Development",
    image: "/rnsinfotech.png",
    technologies: ["IT Solutions", "Business Services", "Technology"],
    link: "#"
  }
];

export default function Portfolio() {
  const [activeCategory, setActiveCategory] = useState("All");

  const filteredItems = portfolioProjects.filter(item => 
    activeCategory === "All" || item.category === activeCategory
  );

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-950 overflow-x-hidden transition-colors duration-300">
      <Navigation />
      
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32 font-roboto bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 transition-colors duration-300">
        {/* Background Elements */}
        <div className="absolute inset-0">
          {/* Subtle Grid Pattern */}
          <div className="absolute inset-0 opacity-5 dark:opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
                               linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)`,
              backgroundSize: '50px 50px',
              maskImage: 'radial-gradient(circle at center, black 40%, transparent 80%)'
            }} />
          </div>
          
          {/* Gradient Orbs */}
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-to-r from-blue-100/50 to-cyan-100/30 dark:from-blue-500/10 dark:to-cyan-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-gradient-to-r from-indigo-100/30 to-purple-100/20 dark:from-indigo-500/5 dark:to-purple-500/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-4 md:px-6 relative z-10">
          {/* Header Section */}
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            {/* Badge */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-blue-500/10 to-cyan-500/10 dark:from-blue-500/20 dark:to-cyan-500/20 backdrop-blur-sm rounded-full border border-blue-200 dark:border-blue-500/30 mb-8"
            >
              <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full animate-pulse"></div>
              <span className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 dark:from-blue-400 dark:to-cyan-300 bg-clip-text text-transparent tracking-wider">
                OUR PORTFOLIO SHOWCASE
              </span>
            </motion.div>
            
            {/* Main Title */}
            <motion.h1
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.7 }}
              className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight text-gray-900 dark:text-white"
            >
              Our{' '}
              <span className="relative inline-block">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 dark:from-blue-400 dark:via-cyan-300 dark:to-teal-400 animate-gradient bg-[length:200%_auto]">
                  Portfolio
                </span>
                {/* Text Shadow Glow */}
                <span className="absolute inset-0 text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-cyan-500 to-teal-500 dark:from-blue-400 dark:via-cyan-300 dark:to-teal-400 blur-2xl opacity-30 animate-gradient bg-[length:200%_auto]">
                  Portfolio
                </span>
              </span>
            </motion.h1>
            
            {/* Description */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.7 }}
              className="text-xl text-gray-600 dark:text-gray-300 max-w-3xl mx-auto leading-relaxed"
            >
              Explore our portfolio of successful projects across various industries and discover how we've helped businesses achieve their goals.
            </motion.p>
          </motion.div>
          
          {/* Filter Buttons */}
          <motion.div 
            className="flex flex-wrap justify-center gap-3 mb-16"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            {categories.map((category) => (
              <motion.button
                key={category}
                onClick={() => setActiveCategory(category)}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className={`relative px-5 py-2.5 rounded-full font-medium transition-all duration-300 overflow-hidden group ${
                  activeCategory === category 
                    ? "bg-gradient-to-r from-blue-600 to-cyan-500 text-white shadow-lg shadow-blue-500/30 dark:shadow-blue-500/20" 
                    : "bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-white dark:hover:bg-gray-700/80 shadow-sm hover:shadow-md border border-gray-200 dark:border-gray-700"
                }`}
              >
                {/* Background Glow Effect */}
                {activeCategory === category && (
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-500 blur-xl opacity-30 dark:opacity-50 animate-pulse"></div>
                )}
                
                <span className="relative z-10 flex items-center gap-2">
                  {category}
                  {activeCategory === category && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="w-2 h-2 bg-white rounded-full"
                    />
                  )}
                </span>
                
                {/* Hover Border Animation */}
                <div className="absolute inset-0 rounded-full border border-transparent group-hover:border-blue-400/30 transition-all duration-300" />
              </motion.button>
            ))}
          </motion.div>
          
          {/* Portfolio Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            <AnimatePresence>
              {filteredItems.map((item, index) => (
                <motion.div
                  key={item.id}
                  layout
                  initial={{ opacity: 0, y: 60, scale: 0.9 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.1,
                    type: "spring",
                    stiffness: 100
                  }}
                  whileHover={{ 
                    y: -8,
                    scale: 1.02,
                    transition: { duration: 0.3 }
                  }}
                  className="relative"
                >
                  {/* Glow Effect */}
                  <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-500/10 via-cyan-400/10 to-teal-400/10 dark:from-blue-500/20 dark:via-cyan-400/20 dark:to-teal-400/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  
                  {/* Card */}
                  <Card className="relative bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-gray-200/80 dark:border-gray-700/80 rounded-2xl overflow-hidden group-hover:border-blue-300/50 dark:group-hover:border-blue-500/30 transition-all duration-300 h-[480px] flex flex-col shadow-lg hover:shadow-xl dark:shadow-gray-900/50">
                    {/* Project Image */}
                    <div className="relative h-48 overflow-hidden flex-shrink-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-4">
                      <motion.div 
                        className="w-full h-full flex items-center justify-center"
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.3 }}
                      >
                        <img 
                          src={item.image} 
                          alt={item.title}
                          className="max-w-full max-h-40 object-contain transition-all duration-300 group-hover:scale-110"
                        />
                      </motion.div>
                      
                      {/* Corner Accents */}
                      <div className="absolute top-4 right-4 w-3 h-3 border-t border-r border-blue-400/30 rounded-tr-lg" />
                      <div className="absolute bottom-4 left-4 w-3 h-3 border-b border-l border-cyan-400/30 rounded-bl-lg" />
                    </div>
                    
                    <CardContent className="p-6 flex-1 flex flex-col">
                      {/* Title */}
                      <div className="flex items-start justify-between mb-4 flex-shrink-0">
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-300 line-clamp-1">
                          {item.title}
                        </h3>
                        <motion.span 
                          className="text-xs font-semibold bg-gradient-to-r from-blue-100 to-cyan-100 dark:from-blue-500/20 dark:to-cyan-500/20 text-blue-700 dark:text-blue-300 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/30 flex-shrink-0 ml-2"
                          whileHover={{ scale: 1.1 }}
                        >
                          {item.category}
                        </motion.span>
                      </div>
                      
                      {/* Description */}
                      <div className="mb-6 flex-1">
                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed line-clamp-3">
                          {item.description}
                        </p>
                      </div>
                      
                      {/* Technologies */}
                      <div className="mt-auto">
                        <div className="flex flex-wrap gap-2">
                          {item.technologies.slice(0, 3).map((tech, techIndex) => (
                            <motion.span
                              key={techIndex}
                              initial={{ opacity: 0, scale: 0.8 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: index * 0.1 + techIndex * 0.1 }}
                              className="text-xs text-gray-700 dark:text-gray-400 bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full border border-gray-200 dark:border-gray-700 group-hover:border-blue-300 dark:group-hover:border-blue-500/30 transition-all duration-300 line-clamp-1"
                            >
                              {tech}
                            </motion.span>
                          ))}
                          {item.technologies.length > 3 && (
                            <span className="text-xs text-gray-500 dark:text-gray-500 bg-gray-100/80 dark:bg-gray-800/80 backdrop-blur-sm px-3 py-1.5 rounded-full flex-shrink-0">
                              +{item.technologies.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      {/* Bottom Gradient Line */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-blue-500 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500" />
                    </CardContent>
                    
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-cyan-500/5 opacity-0 group-hover:opacity-100 transition-all duration-500" />
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Stats Section */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="mt-20 pt-20 border-t border-gray-200 dark:border-gray-800/50"
          >
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
              {[
                { value: "500+", label: "Projects Delivered" },
                { value: "200+", label: "Happy Clients" },
                { value: "10+", label: "Years Experience" },
                { value: "4.9/5", label: "Client Rating" }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center group"
                >
                  <div className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 dark:from-blue-400 dark:to-cyan-300 bg-clip-text text-transparent mb-2">
                    {stat.value}
                  </div>
                  <div className="text-gray-600 dark:text-gray-400 text-sm font-medium">
                    {stat.label}
                  </div>
                  <div className="h-0.5 w-16 bg-gradient-to-r from-blue-500/30 to-cyan-500/30 rounded-full mx-auto mt-4 group-hover:w-24 transition-all duration-300" />
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
        
        {/* Floating Particles */}
        <div className="absolute bottom-20 left-0 right-0">
          <div className="flex justify-center items-center gap-4">
            {[...Array(5)].map((_, i) => (
              <motion.div
                key={i}
                animate={{ y: [0, -10, 0] }}
                transition={{
                  duration: 2,
                  delay: i * 0.2,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
                className="w-2 h-2 bg-gradient-to-r from-blue-500 to-cyan-400 dark:from-blue-400 dark:to-cyan-300 rounded-full opacity-50"
              />
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
